import { action, computed, observable, runInAction } from 'mobx';
import MetricDeviceCollection from 'app/stores/metrics/MetricDeviceCollection';
import $auth from 'app/stores/$auth';
import $app from 'app/stores/$app';
import { modelFinder } from 'app/stores/util/modelFinder';
import MetricDeviceModel from 'app/stores/metrics/MetricDeviceModel';
import { api } from 'core/util';
import { Collection } from 'core/model/Collection';
import { Model } from 'core/model/Model';
import $devices from '../device/$devices';

class MetricsDevicesStore {
  collection = new MetricDeviceCollection();

  @observable.ref
  initializePromise: Promise<unknown> | undefined;

  @observable
  initialized = false;

  initialize() {
    return this.setup();
  }

  @action
  destroy() {
    this.initializePromise = undefined;
    this.initialized = false;
  }

  @action
  setup() {
    if (!this.initializePromise) {
      if ($auth.hasPermission('recon.enabled', { overrideForSudo: false }) && $app.isSharedLink && !$app.isSubtenant) {
        this.initializePromise = this.collection.fetch().then((results) => {
          this.initialized = true;
          return results;
        });
      } else {
        this.initialized = true;
      }
    }
    return this.initializePromise || Promise.resolve();
  }

  /**
   * This is only private to make test mocks easier.
   */
  @computed
  private get _byIp() {
    // @ts-expect-error need to fully convert MetricDeviceModel
    return modelFinder(this.collection, 'ip_address');
  }

  byIp(ip_address: string) {
    return this._byIp(ip_address) as MetricDeviceModel | undefined;
  }

  /**
   * This is only private to make test mocks easier.
   */
  @computed
  private get _byMacAddress() {
    // @ts-expect-error need to fully convert MetricDeviceModel
    return modelFinder(this.collection, 'mac_address');
  }

  byMacAddress(mac_address: string) {
    return this._byMacAddress(mac_address) as MetricDeviceModel | undefined;
  }
}

export const $metricsDevices = new MetricsDevicesStore();

export const deviceModelByName = (deviceName: string): MetricDeviceModel | undefined =>
  $metricsDevices.collection.models.find((model) => {
    const modelDeviceName = `${model.get('name')}`.toLowerCase();
    const matchDeviceName = `${deviceName}`.toLowerCase();
    return modelDeviceName === matchDeviceName;
  });

export const getMetricsDeviceNameFromQueryModel = ({
  device_name,
  device_ip,
  mac_address
}: {
  device_name?: string;
  device_ip?: string;
  mac_address?: string;
}) => {
  if (!device_name && device_ip) {
    device_name = $metricsDevices.byIp(device_ip)?.get('name') as string;
  }
  if (!device_name && mac_address) {
    device_name = $metricsDevices.byMacAddress(mac_address)?.get('name') as string;
  }
  return device_name;
};

export const hasNmsDevices = computed(
  (): boolean =>
    !$app.isSubtenant &&
    ($app.isSharedLink
      ? $metricsDevices.collection.hasFetched && $metricsDevices.collection.unfilteredSize > 0
      : $devices.hasNmsDevices)
);

/**
 * This gets called from the Add ICMP Devices page, but re-use the same API
 * endpoint as the Discovery page.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const createIcmpDevices = <M extends Model<any>>(
  configs: object[],
  collection: Collection<M>
): Promise<unknown> =>
  runInAction(() =>
    api
      .post('/api/ui/discovery/devices', {
        body: { devices: configs }
      })
      .then((results: { address?: string; success?: boolean }[]) => {
        const hasErrors = results.some((result) => result.success === false);
        const failedDevices: M[] = [];

        if (hasErrors) {
          results.forEach((result) => {
            const device = collection.models.find((d) => d.get('address') === result.address) as M | undefined;

            if (device) {
              device.safeSet(result);

              if (result.success === false) {
                failedDevices.push(device);
              }
            }
          });

          collection.selected = failedDevices;

          return Promise.reject();
        }

        return $metricsDevices.collection
          .fetch({ force: true })
          .then(() => $devices.loadDeviceSummaries())
          .then(() => results);
      })
  );
